import React, { useMemo, useState } from 'react';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {
  FrotaCardBody,
  FrotaCardHeader,
  FrotaCardHeaderActions,
  FrotaCardHeaderMain,
  FrotaHeaderActionStack,
  FrotaHeaderBadgeItem,
  FrotaHeaderBadgeLabel,
  FrotaHeaderBadges,
  FrotaCardIconShell,
  FrotaCardSubtitle,
  FrotaCardSurface,
  FrotaCardTitle,
  FrotaCardTitleGroup,
  FrotaExpandButton,
  FrotaFooterGrid,
  FrotaFooterItem,
  FrotaStatusPill,
  FrotaSummaryGrid,
  FrotaSummaryItem,
  FrotaSummaryLabel,
  FrotaSummaryNotice,
  FrotaSummaryNoticeLabel,
  FrotaSummaryNoticeText,
  FrotaSummaryNoticeValue,
  FrotaSummaryText,
  FrotaSummaryValue,
  FrotaTripBlockGrid,
  FrotaTripCell,
  FrotaTripCellLabel,
  FrotaTripCellMeta,
  FrotaTripCellText,
  FrotaTripCellValue
} from '../DisponibilidadeFrota-styled';
import { emptyValue } from '../utils/constants';

const getDisplayValue = value => {
  if (value === null || value === undefined || value === '') return emptyValue;
  return value;
};

const hasDisplayValue = value => value !== null && value !== undefined && value !== '' && value !== emptyValue;

const chunkItems = (items, size = 3) => {
  const chunks = [];

  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }

  return chunks;
};

const AvailabilityCardItem = ({
  title,
  subtitle,
  summaryItems = [],
  detailItems = [],
  expandedContent = null,
  footerItems = [],
  headerIcon = null,
  headerBadge = null,
  headerBadges = [],
  headerControls = null,
  headerActionsJustify = null,
  headerBadgesJustify = null,
  headerBadgesMinWidth = null,
  summaryGridColumns = null,
  summaryNotice = null,
  titleFontSize = null,
  titleTooltip = null,
  subtitleTooltip = null,
  expanded: expandedProp = undefined,
  onExpandedChange = null,
  selected = false
}) => {
  const [expandedState, setExpandedState] = useState(false);
  const detailRows = useMemo(() => chunkItems(detailItems, 3), [detailItems]);
  const hasExpandableContent = Boolean(expandedContent) || detailItems.length > 0 || footerItems.length > 0;
  const expanded = typeof expandedProp === 'boolean' ? expandedProp : expandedState;
  const normalizedHeaderBadges = headerBadges.length
    ? headerBadges.filter(badge => badge?.label)
    : (headerBadge?.label ? [headerBadge] : []);

  const handleExpandClick = event => {
    event.stopPropagation();
    const nextExpanded = !expanded;

    if (typeof expandedProp !== 'boolean') {
      setExpandedState(nextExpanded);
    }

    onExpandedChange?.(nextExpanded);
  };

  return (
    <FrotaCardSurface $selected={selected}>
      <FrotaCardHeader>
        <FrotaCardHeaderMain>
          <FrotaCardIconShell>
            {headerIcon}
          </FrotaCardIconShell>

          <FrotaCardTitleGroup>
            <FrotaCardTitle
              $fontSize={titleFontSize}
              title={getDisplayValue(titleTooltip || title)}
            >
              {getDisplayValue(title)}
            </FrotaCardTitle>
            {hasDisplayValue(subtitle) ? (
              <FrotaCardSubtitle title={getDisplayValue(subtitleTooltip || subtitle)}>
                {getDisplayValue(subtitle)}
              </FrotaCardSubtitle>
            ) : null}
          </FrotaCardTitleGroup>
        </FrotaCardHeaderMain>

        <FrotaCardHeaderActions $justifyContent={headerActionsJustify}>
          {normalizedHeaderBadges.length ? (
            <FrotaHeaderBadges
              $justifyContent={headerBadgesJustify}
              $minWidth={headerBadgesMinWidth}
            >
              {normalizedHeaderBadges.map(badge => (
                <FrotaHeaderBadgeItem key={`${badge.caption || badge.label}-${badge.tone || 'default'}`}>
                  <FrotaHeaderBadgeLabel>{badge.caption || 'Status'}</FrotaHeaderBadgeLabel>
                  <FrotaStatusPill $tone={badge.tone}>
                    {badge.icon ? badge.icon : null}
                    {badge.label}
                  </FrotaStatusPill>
                </FrotaHeaderBadgeItem>
              ))}
            </FrotaHeaderBadges>
          ) : null}

          {(headerControls || hasExpandableContent) ? (
            <FrotaHeaderActionStack $hasSecondaryAction={Boolean(headerControls)}>
              {hasExpandableContent ? (
                <FrotaExpandButton type="button" onClick={handleExpandClick} aria-label="Expandir card">
                  <ExpandMoreIcon
                    style={{
                      fontSize: 28,
                      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease'
                    }}
                  />
                </FrotaExpandButton>
              ) : null}

              {headerControls}
            </FrotaHeaderActionStack>
          ) : null}
        </FrotaCardHeaderActions>
      </FrotaCardHeader>

      <FrotaSummaryGrid $gridTemplateColumns={summaryGridColumns}>
        {summaryItems.map((summaryItem, index) => (
          <FrotaSummaryItem
            key={summaryItem.label}
            $withDivider={index > 0}
          >
            {summaryItem.icon}
            <FrotaSummaryText>
              <FrotaSummaryLabel>{summaryItem.label}</FrotaSummaryLabel>
              <FrotaSummaryValue
                $wrap={summaryItem.wrap}
                $maxLines={summaryItem.maxLines}
                title={getDisplayValue(summaryItem.value)}
              >
                {getDisplayValue(summaryItem.value)}
              </FrotaSummaryValue>
            </FrotaSummaryText>
          </FrotaSummaryItem>
        ))}
      </FrotaSummaryGrid>

      {summaryNotice?.value && summaryNotice.value !== emptyValue ? (
        <FrotaSummaryNotice>
          {summaryNotice.icon ? summaryNotice.icon : null}
          <FrotaSummaryNoticeText>
            <FrotaSummaryNoticeLabel>{summaryNotice.label || 'Mensagem'}</FrotaSummaryNoticeLabel>
            <FrotaSummaryNoticeValue title={getDisplayValue(summaryNotice.value)}>
              {getDisplayValue(summaryNotice.value)}
            </FrotaSummaryNoticeValue>
          </FrotaSummaryNoticeText>
        </FrotaSummaryNotice>
      ) : null}

      {expanded && hasExpandableContent ? (
        <FrotaCardBody>
          {expandedContent}

          {!expandedContent ? detailRows.map((detailRow, rowIndex) => (
            <FrotaTripBlockGrid key={`detail-row-${rowIndex}`}>
              {detailRow.map((detailItem, itemIndex) => (
                <FrotaTripCell key={detailItem.label} $withDivider={itemIndex > 0}>
                  {detailItem.icon}
                  <FrotaTripCellText>
                    <FrotaTripCellLabel $accentColor={detailItem.accentColor}>
                      {detailItem.label}
                    </FrotaTripCellLabel>
                    <FrotaTripCellValue>{getDisplayValue(detailItem.value)}</FrotaTripCellValue>
                    {detailItem.meta ? (
                      <FrotaTripCellMeta>{detailItem.meta}</FrotaTripCellMeta>
                    ) : null}
                  </FrotaTripCellText>
                </FrotaTripCell>
              ))}
            </FrotaTripBlockGrid>
          )) : null}

          {footerItems.length ? (
            <FrotaFooterGrid>
              {footerItems.map((footerItem, index) => (
                <FrotaFooterItem key={footerItem.label} $withDivider={index > 0}>
                  <FrotaSummaryLabel>{footerItem.label}</FrotaSummaryLabel>
                  <FrotaSummaryValue $wrap={true}>
                    {getDisplayValue(footerItem.value)}
                  </FrotaSummaryValue>
                </FrotaFooterItem>
              ))}
            </FrotaFooterGrid>
          ) : null}
        </FrotaCardBody>
      ) : null}
    </FrotaCardSurface>
  );
};

export default AvailabilityCardItem;
